import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { jobsApi } from '../api/jobs'
import type { JobDetail } from '../api/types'

const POLL_MS = 2000
const LIVE: ReadonlySet<string> = new Set(['Pending', 'Running', 'CancellationRequested'])

export function isJobLive(status: string | undefined): boolean {
  return status !== undefined && LIVE.has(status)
}

/** Polls `GET /jobs/{id}` every 2s while the job is still moving. */
export function useJobPolling(jobId: string | undefined) {
  const [job, setJob] = useState<JobDetail | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(Boolean(jobId))
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (!jobId) return
    let cancelled = false

    const stop = () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current)
        timer.current = null
      }
    }

    const tick = async () => {
      try {
        const detail = await jobsApi.get(jobId)
        if (cancelled) return
        setJob(detail)
        setError(null)
        if (isJobLive(detail.status)) timer.current = window.setTimeout(tick, POLL_MS)
      } catch (cause: unknown) {
        if (cancelled) return
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'NETWORK', String(cause)))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void tick()
    return () => {
      cancelled = true
      stop()
    }
  }, [jobId])

  return { job, error, loading, setJob }
}
